import {
  ApiDayConstraints,
  ApiGeoUnit,
  ApiGeoUnitWithDayConstraints,
  ApiGetGeoUnitRulesRequestFromJSON,
  ApiGetGeoUnitRulesResponse,
  ApiWeekdays
} from '@ibe/api';
import { CamperSearchParams } from './DefaultCamperSearchSchema';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { ApiService, clone, ConfigModel } from '@ibe/services';
import { cloneDeep } from 'lodash-es';
import { WeekDayConstraints } from '../../Components/Input/DateRangeInput/DateRangeInputProps';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export type DateConstraintsReturn = Date | number | WeekDayConstraints[] | undefined;

type SingleDateConstraint = {
  minDate?: Date;
  maxDate?: Date;
  minDateRange?: number;
  weekDayConstraints?: WeekDayConstraints[];
  [key: string]: DateConstraintsReturn;
};

type DateConstraints = Record<'pickup' | 'dropoff', SingleDateConstraint>;

const defaultSearchParams: CamperSearchParams = {
  pickupStationGeoUnit: undefined,
  pickupStationParentGeoUnit: undefined,
  dropOffStationGeoUnit: undefined,
  dropOffStationParentGeoUnit: undefined,
  pickupStation: '',
  dropOffStation: '',
  startDate: undefined,
  endDate: undefined,
  adults: '2',
  children: '0'
};

const findStation = (
  geoUnits: ApiGeoUnitWithDayConstraints[],
  stationCode?: string
): ApiGeoUnitWithDayConstraints | undefined => {
  return geoUnits.reduce(
    (total, current) =>
      (Object.entries(current.children).find(([, child]) => child.code === stationCode) || [
        '',
        undefined
      ])[1] || total,
    undefined as ApiGeoUnit | undefined
  ) as ApiGeoUnitWithDayConstraints | undefined;
};

export const camperFillWithDefaults = (
  values: Partial<CamperSearchParams>,
  config: ConfigModel,
  geoUnits?: ApiGeoUnitWithDayConstraints[]
): CamperSearchParams => {
  const returnValues = clone(defaultSearchParams) as CamperSearchParams;
  const startDate = values.startDate || returnValues.startDate;
  const endDate = values.endDate || returnValues.endDate;
  const adults = values.adults || returnValues.adults;
  const children = values.children || returnValues.children;

  returnValues.adults = adults;
  returnValues.children = children;
  returnValues.startDate = !!startDate ? dayjs(startDate).format(config.formatDate) : undefined;
  returnValues.endDate = !!endDate ? dayjs(endDate).format(config.formatDate) : undefined;
  returnValues.pickupStation = values.pickupStation || returnValues.pickupStation;
  returnValues.pickupStationParentGeoUnit =
    values.pickupStationParentGeoUnit || returnValues.pickupStationParentGeoUnit;
  returnValues.dropOffStation = values.dropOffStation || returnValues.dropOffStation;
  returnValues.dropOffStationParentGeoUnit =
    values.dropOffStationParentGeoUnit || returnValues.dropOffStationParentGeoUnit;
  returnValues.pickupStationGeoUnit =
    values.pickupStationGeoUnit ||
    returnValues.pickupStationGeoUnit ||
    findStation(geoUnits || [], values.pickupStation);
  returnValues.dropOffStationGeoUnit =
    values.dropOffStationGeoUnit ||
    returnValues.dropOffStationGeoUnit ||
    findStation(geoUnits || [], values.dropOffStation);
  returnValues.adults = values.adults;

  return returnValues;
};

export const getRelatedGeoUnitRules = (
  geoUnitRules: ApiGetGeoUnitRulesResponse,
  station: ApiGeoUnitWithDayConstraints,
  geoUnits: ApiGeoUnitWithDayConstraints[]
): ApiGetGeoUnitRulesResponse => {
  const destinations = [
    ...(geoUnitRules.destinations || []),
    { ...station, dayConstraints: geoUnitRules.dayConstraints }
  ];
  const parents = cloneDeep([...geoUnits]);
  parents.forEach(parent => {
    Object.entries(parent.children).forEach(([key, child]) => {
      const foundDestination = destinations.find(destination => destination.code === child.code);
      if (!foundDestination) {
        delete parent.children[key];
      } else {
        parent.children = {
          ...parent.children,
          [key]: { ...foundDestination, name: parent.children[key].name }
        };
      }
    });
  });
  const newDestinations = parents.filter(
    parent => Object.keys(parent.children).length > 0
  ) as ApiGeoUnitWithDayConstraints[];
  return {
    ...geoUnitRules,
    destinations: newDestinations
  };
};

export const getFilteredWeeksDays = (weekDays?: ApiWeekdays): number[] | undefined => {
  if (!weekDays || (!!weekDays && Object.keys(weekDays).length === 0)) return undefined;
  const weekDayArray = [];
  if (!weekDays.monday) weekDayArray.push(1);
  if (!weekDays.tuesday) weekDayArray.push(2);
  if (!weekDays.wednesday) weekDayArray.push(3);
  if (!weekDays.thursday) weekDayArray.push(4);
  if (!weekDays.friday) weekDayArray.push(5);
  if (!weekDays.saturday) weekDayArray.push(6);
  if (!weekDays.sunday) weekDayArray.push(0);
  if (weekDayArray.length > 0) {
    return weekDayArray;
  } else {
    return undefined;
  }
};

export const getWeekDayConstraints = (
  dayConstraints?: ApiDayConstraints[]
): WeekDayConstraints[] | undefined => {
  if (!dayConstraints || (dayConstraints && dayConstraints.length === 0)) return undefined;
  return dayConstraints.map(constraint => ({
    fromDate: constraint.fromDate,
    toDate: constraint.toDate,
    weekDays: getFilteredWeeksDays(constraint.weekdays)
  }));
};

export const getDateConstraints = (
  isChecked: boolean,
  pickupStation?: ApiGeoUnitWithDayConstraints,
  dropOffStation?: ApiGeoUnitWithDayConstraints,
  relatedGeoUnitRules?: ApiGetGeoUnitRulesResponse,
  pickupDate?: string
): DateConstraints | undefined => {
  const findMinDateRange = (): number | undefined => {
    const foundConstraint = relatedGeoUnitRules?.dayConstraints?.find(
      constraint =>
        dayjs(pickupDate).isAfter(dayjs(constraint.fromDate).subtract(1, 'day')) &&
        dayjs(pickupDate).isBefore(dayjs(constraint.toDate).add(1, 'day'))
    );
    return foundConstraint?.minDuration ? foundConstraint.minDuration - 1 : undefined;
  };

  if (!pickupStation || !dropOffStation || !relatedGeoUnitRules) return;
  const pickup = relatedGeoUnitRules;
  const dropoff = isChecked ? relatedGeoUnitRules : dropOffStation;
  const minDate = new Date();
  minDate.setDate(new Date().getDate() + 8);
  return {
    pickup: {
      minDate:
        !!pickup?.dayConstraints && !!pickup.dayConstraints[0].fromDate
          ? dayjs(pickup.dayConstraints[0].fromDate).isBefore(dayjs(minDate))
            ? minDate
            : new Date(pickup.dayConstraints[0].fromDate)
          : minDate,
      maxDate:
        pickup?.dayConstraints && pickup.dayConstraints[pickup.dayConstraints.length - 1]
          ? new Date(pickup.dayConstraints[pickup.dayConstraints.length - 1].toDate || '')
          : undefined,
      minDateRange: findMinDateRange(),
      weekDayConstraints: getWeekDayConstraints(pickup?.dayConstraints)
    },
    dropoff: {
      minDate:
        !!dropoff?.dayConstraints && !!dropoff.dayConstraints[0].fromDate
          ? dayjs(dropoff.dayConstraints[0].fromDate).isBefore(dayjs(minDate))
            ? minDate
            : new Date(dropoff.dayConstraints[0].fromDate)
          : minDate,
      maxDate:
        dropoff?.dayConstraints && dropoff.dayConstraints[dropoff.dayConstraints.length - 1]
          ? new Date(dropoff.dayConstraints[dropoff.dayConstraints.length - 1].toDate || '')
          : undefined,
      minDateRange: findMinDateRange(),
      weekDayConstraints: getWeekDayConstraints(dropoff.dayConstraints)
    }
  };
};

const getFinalDate = (
  date: string,
  dateConstraints?: SingleDateConstraint,
  startDate?: string
): string => {
  let finalDate = date;
  if (dayjs(finalDate).isBefore(dayjs(dateConstraints?.minDate))) {
    finalDate = dayjs(dateConstraints?.minDate).format('YYYY-MM-DD');
  } else if (dayjs(finalDate).isAfter(dayjs(dateConstraints?.maxDate))) {
    finalDate = dayjs(dateConstraints?.maxDate).format('YYYY-MM-DD');
  }

  dateConstraints?.weekDayConstraints?.forEach(
    (weekdayConstraint: WeekDayConstraints, idx: number) => {
      if (
        dayjs(finalDate).isSameOrAfter(dayjs(weekdayConstraint.fromDate), 'day') &&
        dayjs(finalDate).isSameOrBefore(dayjs(weekdayConstraint.toDate), 'day')
      ) {
        weekdayConstraint?.weekDays
          ?.sort((a, b) => a - b)
          .forEach(weekDay => {
            if (dayjs(finalDate).day() === weekDay) {
              finalDate = dayjs(finalDate).add(1, 'day').format('YYYY-MM-DD');
            }
          });
      }
      const nextFromDate = dateConstraints?.weekDayConstraints?.[idx + 1]?.fromDate;
      if (
        !!nextFromDate &&
        dayjs(weekdayConstraint.toDate).diff(dayjs(nextFromDate), 'day') < -1 &&
        dayjs(finalDate).isAfter(weekdayConstraint.toDate, 'day') &&
        dayjs(finalDate).isBefore(dayjs(nextFromDate))
      ) {
        finalDate = nextFromDate;
      }
    }
  );
  if (
    !!startDate &&
    dayjs(date).isSameOrBefore(dayjs(startDate).add(dateConstraints?.minDateRange || 0, 'day'))
  ) {
    finalDate = dayjs(startDate)
      .add(dateConstraints?.minDateRange || 0, 'day')
      .format('YYYY-MM-DD');
  }
  return finalDate;
};

export const findValidStartAndEndDates = async (
  searchParams: Partial<CamperSearchParams>,
  geoUnits: ApiGeoUnitWithDayConstraints[],
  api: ApiService
): Promise<{ startDate?: string; endDate?: string }> => {
  if (!!searchParams.startDate && !!searchParams.endDate) {
    return { startDate: searchParams.startDate, endDate: searchParams.endDate };
  }
  const startDate = searchParams.startDate || dayjs().add(30, 'day').format('YYYY-MM-DD');
  const pickupStation = geoUnits
    .flatMap(geoUnit => Object.entries(geoUnit.children).map(([, child]) => child))
    .find(unit => unit.code === searchParams.pickupStation);

  const geoUnitRules = !!pickupStation?.id
    ? await api.getGeoUnitRules(
        ApiGetGeoUnitRulesRequestFromJSON({
          geoUnitId: pickupStation?.id
        })
      )
    : {};
  const pickupStationWithConstraints = !!geoUnitRules?.destinations
    ? ({
        ...pickupStation,
        dayConstraints: geoUnitRules.dayConstraints
      } as ApiGeoUnitWithDayConstraints)
    : undefined;
  const dropOffStationWithConstraints = geoUnitRules?.destinations?.find(
    destination => destination.code === searchParams.dropOffStation
  );

  if (!pickupStationWithConstraints || !dropOffStationWithConstraints) {
    return { startDate: searchParams.startDate, endDate: searchParams.endDate };
  }
  const newRelatedGeoUnitRules = getRelatedGeoUnitRules(
    geoUnitRules,
    pickupStationWithConstraints,
    geoUnits
  );
  const dateConstraints = getDateConstraints(
    false,
    pickupStationWithConstraints,
    dropOffStationWithConstraints,
    newRelatedGeoUnitRules,
    startDate
  );
  const finalStartDate = getFinalDate(startDate, dateConstraints?.pickup);
  const finalEndDate = getFinalDate(
    dayjs(finalStartDate).add(10, 'day').format('YYYY-MM-DD'),
    dateConstraints?.dropoff,
    finalStartDate
  );

  return {
    startDate: finalStartDate,
    endDate: finalEndDate
  };
};
